/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import {
    CheckCircle,
    Clear,
    DriveFileMoveRounded,
    FileDownloadOutlined,
    FileUploadOutlined,
    Grading,
    PlayCircle,
    Report,
    Search,
    Settings,
} from "@mui/icons-material";
import { Box, Button, Card, CardContent, Grid, Tooltip } from "@mui/material";
import {
    GridActionsCellItem,
    GridRowId,
    GridRowModel,
    GridRowSelectionModel,
    GridSortDirection,
    GridSortModel,
} from "@mui/x-data-grid";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import Checkbox from "@mui/material/Checkbox";
import { useHistory } from "react-router-dom";
import { LoadingButton } from "@mui/lab";
import { toast } from "react-toastify";
import useThrowAsyncError from "@/hooks/useThrowAsyncError";
import { format, parseISO } from "date-fns";

import AutoComplete from "@/components/hookForms/autoComplete";
import { Container } from "@/theme/globalStyles";
import DataTable from "@/components/dataTable";
import { useAuth } from "@/context/global";
import Datapicker from "@/components/hookForms/datePicker";
import IGenericResult from "@/interfaces/IGenericResult";
import serviceRebalanceUsina from "@/services/serviceRebalanceUsina";
import AutoCompleteCheckbox from "@/components/hookForms/autoCompleteCheckbox";
import serviceConcessionaria from "@/services/serviceConcessionaria";
import { tipoCalculoDialogOptions, tipoRebateFilterOptions } from "@/utils/constants";
import DialogInfo from "@/components/informationDialog";
import DialogImport from "./modal/dialogImport";
import DialogError from "./modal/dialogError";
import serviceRebateUsina from "@/services/serviceRebateUsina";
import { downloadFileFromBase64 } from "@/utils/utils";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const CalculoRebate = (props: any) => {
    const { setTitlePage } = useAuth();
    const history = useHistory();

    useEffect(() => {
        setTitlePage("Análise Tarifária - Cálculo de Rebate");
    }, [setTitlePage]);

    const [pageSize, setPageSize] = useState(100);
    const [page, setPage] = useState(0);
    const [rowCount, setRowCount] = useState(0);
    const [linesSelecteds, setLinesSelecteds] = useState<GridRowSelectionModel>([]);
    const [filters, setFilters] = useState(null);
    const [data, setData] = useState<any[]>([]);
    const [statusDataOptions, setStatusDataOptions] = useState<any[]>([]);
    const [concessionariaDataOptions, setConcessionariaDataOptions] = useState<IGenericResult[]>([]);
    const [usinaDataOptions, setUsinaDataOptions] = useState<any[]>([]);
    const [dataMessages, setDataMessages] = useState<string[]>([]);
    const [infoModal, setInfoModal] = useState(false);
    const [openImport, setOpenImport] = useState(false);
    const [errorState, setErrorState] = useState({ open: false, rebateID: "" });

    const [iniciarDisabled, setIniciarDisabled] = useState(false);
    const [downloadDisabled, setDownloadDisabled] = useState(false);
    const [processarDisabled, setProcessarDisabled] = useState(false);
    const [aprovarDisabled, setAprovarDisabled] = useState(false);
    const [reabrirDisabled, setReabrirDisabled] = useState(false);

    const [isLoading, setLoading] = useState(false);
    const [iniciarLoading, setIniciarLoading] = useState(false);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [processarLoading, setProcessarLoading] = useState(false);
    const [aprovarLoading, setAprovarLoading] = useState(false);
    const [reabrirLoading, setReabrirLoading] = useState(false);

    const [sortModel, setSortModel] = useState<GridSortModel>([
        {
            field: "",
            sort: "" as GridSortDirection,
        },
    ]);

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
        // eslint-disable-next-line react-hooks/rules-of-hooks
    } = useForm({
        defaultValues: {
            cicloFaturamento: null || new Date(),
            concessionariaId: "",
            usinasIds: "",
            tipoRebate: "",
            tipoCalculo: "",
            statusId: "",
        },
    });

    const actionColumns = useMemo(
        () => [
            {
                headerName: "Operação",
                field: "operations",
                type: "actions",
                width: 400,
                hide: false,
                getActions: (params: { id: GridRowId; row: GridRowModel }) => [
                    <GridActionsCellItem
                        icon={
                            <Tooltip title="Detalhar">
                                <Grading />
                            </Tooltip>
                        }
                        label="Detalhar"
                        onClick={() => {
                            history.push("/analisetarifaria/detalhesrebate", {
                                rebateID: params.row.rebateID,
                                status: params.row.status,
                                filterOld: filters,
                            });
                        }}
                        disabled={params.row.status === "A iniciar" || iniciarDisabled}
                    />,
                    <GridActionsCellItem
                        icon={
                            <Tooltip title="Erros">
                                <Report />
                            </Tooltip>
                        }
                        label="Erros"
                        onClick={() => {
                            setErrorState({
                                open: true,
                                rebateID: params.row.rebateID.toString(),
                            });
                        }}
                        disabled={params.row.status.toLowerCase() !== "erro"}
                    />,
                ],
            },
        ],
        []
    );

    const columns = useMemo(
        () => [
            {
                headerName: "ID",
                field: "rebateID",
                type: "string",
                headerAlign: "left",
                identity: true,
                minWidth: 70,
                flex: 1,
            },
            {
                headerName: "Referência",
                field: "cicloFaturamento",
                type: "string",
                headerAlign: "left",
                minWidth: 100,
                flex: 1,
                valueGetter: (params: { row: GridRowModel }) =>
                    params.row.cicloFaturamento ? parseISO(params.row.cicloFaturamento) : "",
                valueFormatter: (item: any) => {
                    return item.value ? format(item.value, "dd/MM/yyyy") : "";
                },
            },
            {
                headerName: "Concessionária",
                field: "concessionariaNome",
                type: "string",
                headerAlign: "left",
                minWidth: 250,
                flex: 1,
            },
            {
                headerName: "Usina",
                field: "usinaNome",
                type: "string",
                headerAlign: "left",
                minWidth: 250,
                flex: 1,
            },
            {
                headerName: "Status",
                field: "status",
                type: "string",
                headerAlign: "left",
                minWidth: 250,
                flex: 1,
            },
            {
                headerName: "Tipo Rebate",
                field: "tipoRebate",
                type: "string",
                headerAlign: "left",
                minWidth: 180,
                flex: 1,
            },
            {
                headerName: "Tipo de Cálculo",
                field: "tipoCalculo",
                type: "string",
                headerAlign: "left",
                minWidth: 180,
                flex: 1,
            },
            {
                headerName: "Tipo de Pesquisa Cliente",
                field: "referenciaRebalance",
                type: "string",
                minWidth: 200,
                flex: 1,
            },
            {
                headerName: "Qtd Ucs",
                field: "quantidadeUCs",
                type: "string",
                headerAlign: "left",
                minWidth: 180,
                flex: 1,
            },
            ...actionColumns,
        ],
        [actionColumns]
    );
    useEffect(() => {
        setStatusData();
        setConcessionariaData();
        getAllWithFilters(filters);
    }, []);

    useEffect(() => {
        if (props && props.location && props.location.state) {
            setFilters(props.location.state);
            const newValues = {
                ...props.location.state,
                usinasIds: Array.isArray(props.location.state.usinasIds)
                    ? props.location.state.usinasIds.join(",")
                    : props.location.state.usinasIds,
            };

            getUsinaData({ concessionariaID: props.location.state.concessionariaID });
            getAllWithFilters(newValues);
            reset({ ...props.location.state });
        }
    }, [props]);

    useEffect(() => {
        getAllWithFilters(filters);
    }, [page, pageSize, sortModel]);

    const throwAsyncError = useThrowAsyncError();

    const getAllWithFilters = async (filters: any) => {
        setData([]);
        setLoading(true);

        const newValues = {
            ...filters,
            concessionariaId: filters?.concessionariaId || "",
            cicloFaturamento: filters?.cicloFaturamento
                ? format(filters?.cicloFaturamento, "MM/yyyy")
                : format(new Date(), "MM/yyyy"),
        };

        serviceRebateUsina
            .getWithFilter({
                ...newValues,
                take: pageSize,
                currentPage: page + 1,
                sortExpression: sortModel[0]?.field ?? "",
                sortDirection: sortModel[0]?.sort ?? "",
            })
            .then((result) => {
                if (!result) {
                    throw new Error("Falha ao carregar os dados.");
                }
                setData(result.data.content);
                setRowCount(result.data.numberOfElements);
            })
            .catch((err) => {
                setData([]);
                setRowCount(0);
                throwAsyncError(err);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    function serviceStart(ids) {
        setIniciarDisabled(true);
        setIniciarLoading(true);
        setLoading(true);

        serviceRebateUsina
            .start(ids)
            .then((response) => {
                if (response) {
                    toast.info("O Rebate será iniciado para a(s) usina(s) selecionada(s), por favor aguarde.");
                    getAllWithFilters(filters);
                }
            })
            .catch(() => {
                toast.error("Erro ao Iniciar o Rebate para a(s) usina(s) selecionada(s), tente novamente mais tarde.", {
                    autoClose: 2000,
                });
            })
            .finally(() => {
                setIniciarDisabled(false);
                setIniciarLoading(false);
                setLoading(false);
                setLinesSelecteds([]);
            });
    }

    function serviceProcess(ids, cicloFaturamento) {
        setProcessarDisabled(true);
        setProcessarLoading(true);
        setLoading(true);

        const newValues = {
            UsinasIds: ids.join(","),
            mes: cicloFaturamento.toString().substring(0, 2),
            ano: cicloFaturamento.toString().substring(3),
        };

        serviceRebateUsina
            .process(newValues)
            .then((response) => {
                if (response) {
                    toast.info("O Rebate será processado para a(s) usina(s) selecionada(s), por favor aguarde.");
                    getAllWithFilters(filters);
                }
            })
            .finally(() => {
                setProcessarDisabled(false);
                setProcessarLoading(false);
                setLoading(false);
            });
    }

    function serviceDownload(ids, cicloFaturamento) {
        setDownloadDisabled(true);
        setDownloadLoading(true);
        setLoading(true);

        const newValues = {
            rebateUsinasIds: ids.join(","),
            mes: cicloFaturamento.toString().substring(0, 2),
            ano: cicloFaturamento.toString().substring(3),
        };

        serviceRebateUsina
            .download(newValues)
            .then((base64) => {
                if (base64 && base64.data) {
                    downloadFileFromBase64(base64.data, "rebate", "csv");
                }
            })
            .finally(() => {
                setDownloadDisabled(false);
                setDownloadLoading(false);
                setLoading(false);
            });
    }

    function serviceApprove(ids) {
        setAprovarDisabled(true);
        setAprovarLoading(true);
        setLoading(true);

        const newValues = {
            rebateUsinaIds: ids.join(","),
        };

        serviceRebateUsina
            .approve(newValues)
            .then((response) => {
                if (response) {
                    toast.success("Rebate aprovado com sucesso para a(s) usina(s) selecionada(s).");
                    getAllWithFilters(filters);
                }
            })
            .finally(() => {
                setAprovarDisabled(false);
                setAprovarLoading(false);
                setLoading(false);
            });
    }

    function serviceReopen(ids) {
        setReabrirDisabled(true);
        setReabrirLoading(true);
        setLoading(true);

        const newValues = {
            rebateUsinaIds: ids.join(","),
        };

        serviceRebateUsina
            .reopen(newValues)
            .then((response) => {
                if (response) {
                    toast.success("Rebate reaberto com sucesso para a(s) usina(s) selecionada(s).");
                    getAllWithFilters(filters);
                }
            })
            .finally(() => {
                setReabrirDisabled(false);
                setReabrirLoading(false);
                setLoading(false);
            });
    }

    function execList(verb: string) {
        const selecteds = data.filter((item) => linesSelecteds.includes(item.rebateID));

        let arrayMessages: string[] = [];
        let listIds: number[] = [];

        if (selecteds.length > 0) {
            selecteds.forEach((value) => {
                if (verb === "start") {
                    if (value.status.toLowerCase() === "a iniciar" || value.status.toLowerCase() === "erro") {
                        listIds.push(value.rebateID);
                    } else {
                        arrayMessages.push(
                            `Cálculo Rebate já iniciado para a usina: ${value.usinaNome} - ID: ${value.rebateID}`
                        );
                    }
                } else if (verb === "process") {
                    if (
                        value.status.toLowerCase() === "a iniciar" ||
                        value.status.toLowerCase() === "aprovado" ||
                        value.status.toLowerCase() === "aguardando consolidação"
                    ) {
                        arrayMessages.push(
                            `Cálculo Rebate não pode ser processado para a usina: ${value.usinaNome} - ID: ${value.rebateID}`
                        );
                    } else {
                        listIds.push(value.rebateID);
                    }
                } else if (verb === "download") {
                    listIds.push(value.rebateID);
                } else if (verb === "approve") {
                    if (value.status.toLowerCase() === "processado" || value.status.toLowerCase() === "erro") {
                        listIds.push(value.rebateID);
                    } else {
                        arrayMessages.push(
                            `Cálculo Rebate não pode ser aprovado para a usina: ${value.usinaNome} - ID: ${value.rebateID}`
                        );
                    }
                } else if (verb === "reopen") {
                    if (value.status.toLowerCase() === "aprovado" || value.status.toLowerCase() === "erro") {
                        listIds.push(value.rebateID);
                    } else {
                        arrayMessages.push(
                            `Cálculo Rebate não pode ser reaberto para a usina: ${value.usinaNome} - ID: ${value.rebateID}`
                        );
                    }
                }
            });
        }

        if (arrayMessages.length > 0) {
            setDataMessages(arrayMessages);
            return;
        }

        // executa requisição
        const concatIds = listIds;

        let ciclo: any;
        let activeFilters: any = filters;

        if (activeFilters && activeFilters.cicloFaturamento) {
            ciclo = format(activeFilters.cicloFaturamento, "MM/yyyy");
        } else {
            ciclo = format(new Date(), "MM/yyyy");
        }

        if (verb === "start") {
            serviceStart(concatIds);
        } else if (verb === "process") {
            serviceProcess(concatIds, ciclo);
        } else if (verb === "download") {
            serviceDownload(concatIds, ciclo);
        } else if (verb === "approve") {
            serviceApprove(concatIds);
        } else if (verb === "reopen") {
            serviceReopen(concatIds);
        }
    }

    useEffect(() => {
        if (dataMessages.length > 0) setInfoModal(true);
    }, [dataMessages]);

    const setConcessionariaData = async () => {
        serviceConcessionaria
            .getConcessionarias()
            .then((result) => {
                setConcessionariaDataOptions(result?.data);
            })
            .catch(() => {
                setConcessionariaDataOptions([]);
            });
    };

    const setStatusData = async () => {
        serviceRebateUsina
            .getStatus()
            .then((result) => {
                if (result) {
                    setStatusDataOptions(result.data);
                }
            })
            .catch(() => {
                setStatusDataOptions([]);
            });
    };

    const getUsinaData = async (values: any) => {
        if (values.concessionariaID) {
            serviceRebalanceUsina
                .getUsinasByConcessionaria(values)
                .then((result) => {
                    if (result) {
                        setUsinaDataOptions(result?.data);
                    }
                })
                .catch(() => {
                    setUsinaDataOptions([]);
                });
        }
    };

    const onClear = () => {
        reset({
            cicloFaturamento: null || new Date(),
            concessionariaId: "",
            usinasIds: "",
            tipoRebate: "",
            tipoCalculo: "",
            statusId: "",
        });
        setFilters(null);
        getAllWithFilters(null);
    };

    const onSubmit = (values: any) => {
        const newFilters = {
            ...values,
            usinasIds: values.usinasIds ? values.usinasIds.join(",") : "",
            tipoRebate: typeof values.tipoRebate === "number" ? values.tipoRebate - 1 : "",
            tipoCalculo: typeof values.tipoCalculo === "number" ? values.tipoCalculo - 1 : "",
        };

        setFilters(newFilters);
        getAllWithFilters(newFilters);
    };

    const onSuccessAction = () => {
        getAllWithFilters(filters);
    };

    return (
        <Container>
            <Card>
                <CardContent>
                    <Box sx={{ mb: 2 }}>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <Grid container spacing={2} mt={2}>
                                <Grid item md={3} xs={12}>
                                    <Datapicker
                                        name="cicloFaturamento"
                                        label="Referência"
                                        control={control}
                                        error={errors}
                                        format="MM/yyyy"
                                        view={["month", "year"]}
                                        noDateForward
                                    />
                                </Grid>
                                <Grid item md={3} xs={12}>
                                    <AutoComplete
                                        name="concessionariaId"
                                        label="Concessionária"
                                        options={concessionariaDataOptions ?? []}
                                        control={control}
                                        error={errors}
                                        onChange={(idConcessionaria) => {
                                            if (idConcessionaria) {
                                                getUsinaData({ concessionariaID: idConcessionaria });
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item md={3} xs={12}>
                                    <AutoCompleteCheckbox
                                        name="usinasIds"
                                        label="Usinas"
                                        options={usinaDataOptions ?? []}
                                        control={control}
                                        error={errors}
                                        limitTags={1}
                                        disableCloseOnSelect={true}
                                        renderOption={(props, option, { selected }) => (
                                            <li {...props}>
                                                <Checkbox
                                                    icon={icon}
                                                    checkedIcon={checkedIcon}
                                                    style={{ marginRight: 8 }}
                                                    checked={selected}
                                                />
                                                {option.label}
                                            </li>
                                        )}
                                    />
                                </Grid>
                                <Grid item md={3} xs={12}>
                                    <Button
                                        disableElevation
                                        variant="contained"
                                        color="warning"
                                        fullWidth
                                        style={{ margin: "16px 0px 8px 0px" }}
                                        startIcon={<Search />}
                                        type="submit"
                                    >
                                        Pesquisar
                                    </Button>
                                </Grid>
                            </Grid>
                            <Grid container spacing={2}>
                                <Grid item md={3} xs={12}>
                                    <AutoComplete
                                        name="tipoRebate"
                                        label="Tipo Rebate"
                                        options={tipoRebateFilterOptions}
                                        control={control}
                                        error={errors}
                                    />
                                </Grid>
                                <Grid item md={3} xs={12}>
                                    <AutoComplete
                                        name="tipoCalculo"
                                        label="Tipo de Cálculo"
                                        options={tipoCalculoDialogOptions}
                                        control={control}
                                        error={errors}
                                    />
                                </Grid>
                                <Grid item md={3} xs={12}>
                                    <AutoComplete
                                        name="statusId"
                                        label="Status"
                                        options={
                                            statusDataOptions.map((data) => {
                                                return { value: data.rebateStatusID, label: data.descricao };
                                            }) ?? []
                                        }
                                        control={control}
                                        error={errors}
                                    />
                                </Grid>
                                <Grid item md={3} xs={12}>
                                    <Button
                                        disableElevation
                                        variant="outlined"
                                        color="warning"
                                        fullWidth
                                        style={{ margin: "16px 0px 8px 0px" }}
                                        startIcon={<Clear />}
                                        onClick={() => onClear()}
                                    >
                                        Limpar
                                    </Button>
                                </Grid>
                            </Grid>
                        </form>
                        <Grid container spacing={2}>
                            <Grid item md={12}>
                                <Grid container spacing={2}>
                                    <Grid item md={2} xs={12}>
                                        <LoadingButton
                                            loading={iniciarLoading}
                                            disableElevation
                                            variant="contained"
                                            color="primary"
                                            fullWidth
                                            style={{ margin: "16px 0px 8px 0px" }}
                                            startIcon={<PlayCircle />}
                                            onClick={() => execList("start")}
                                            disabled={linesSelecteds.length <= 0 || iniciarDisabled}
                                        >
                                            Iniciar
                                        </LoadingButton>
                                    </Grid>
                                    <Grid item md={2} xs={12}>
                                        <LoadingButton
                                            loading={downloadLoading}
                                            disableElevation
                                            variant="contained"
                                            color="primary"
                                            fullWidth
                                            style={{ margin: "16px 0px 8px 0px" }}
                                            startIcon={<FileDownloadOutlined />}
                                            onClick={() => execList("download")}
                                            disabled={linesSelecteds.length <= 0 || downloadDisabled}
                                        >
                                            Download
                                        </LoadingButton>
                                    </Grid>
                                    <Grid item md={2} xs={12}>
                                        <Button
                                            disableElevation
                                            variant="contained"
                                            color="primary"
                                            fullWidth
                                            style={{ margin: "16px 0px 8px 0px" }}
                                            startIcon={<FileUploadOutlined />}
                                            onClick={() => setOpenImport(true)}
                                        >
                                            Importar
                                        </Button>
                                    </Grid>
                                    <Grid item md={2} xs={12}>
                                        <LoadingButton
                                            loading={processarLoading}
                                            disableElevation
                                            variant="contained"
                                            color="primary"
                                            fullWidth
                                            style={{ margin: "16px 0px 8px 0px" }}
                                            startIcon={<Settings />}
                                            onClick={() => execList("process")}
                                            disabled={linesSelecteds.length <= 0 || processarDisabled}
                                        >
                                            Processar
                                        </LoadingButton>
                                    </Grid>

                                    <Grid item md={2} xs={12}>
                                        <LoadingButton
                                            loading={aprovarLoading}
                                            disableElevation
                                            variant="contained"
                                            color="primary"
                                            fullWidth
                                            style={{ margin: "16px 0px 8px 0px" }}
                                            startIcon={<CheckCircle />}
                                            onClick={() => execList("approve")}
                                            disabled={linesSelecteds.length <= 0 || aprovarDisabled}
                                        >
                                            Aprovar
                                        </LoadingButton>
                                    </Grid>
                                    <Grid item md={2} xs={12}>
                                        <LoadingButton
                                            loading={reabrirLoading}
                                            disableElevation
                                            variant="contained"
                                            color="primary"
                                            fullWidth
                                            style={{ margin: "16px 0px 8px 0px" }}
                                            startIcon={<DriveFileMoveRounded />}
                                            onClick={() => execList("reopen")}
                                            disabled={linesSelecteds.length <= 0 || reabrirDisabled}
                                        >
                                            Reabrir
                                        </LoadingButton>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Box>
                    <Box sx={{ height: 450 }}>
                        <DataTable
                            columns={columns}
                            rows={data}
                            rowId={(row: GridRowModel) => row.rebateID}
                            rowCount={rowCount}
                            isLoading={isLoading}
                            onPageChange={(currentPage: number) => setPage(currentPage)}
                            onPageSizeChange={(currentPageSize: number) => setPageSize(currentPageSize)}
                            pageSize={pageSize}
                            checkboxSelection
                            onRowSelectionModelChange={(newSelectionModel) => {
                                setLinesSelecteds(newSelectionModel);
                            }}
                            rowSelectionModel={linesSelecteds}
                            onSortModelChange={(model: GridSortModel) => setSortModel(model)}
                        />
                    </Box>
                </CardContent>
            </Card>
            {infoModal && (
                <DialogInfo
                    data={dataMessages}
                    onClose={() => {
                        setInfoModal(false);
                        setDataMessages([]);
                    }}
                />
            )}
            {openImport && (
                <DialogImport
                    onClose={() => {
                        setOpenImport(false);
                    }}
                    onSuccess={() => onSuccessAction()}
                />
            )}
            <DialogError
                open={errorState.open}
                onSuccess={() => onSuccessAction()}
                data={{
                    open: errorState.open,
                    rebateID: errorState.rebateID,
                }}
                onClose={() => {
                    setErrorState({ open: false, rebateID: "" });
                }}
            />
        </Container>
    );
};

export default CalculoRebate;
